import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { readMessage, markAllAsRead } from '@Store/slices/notificationsSlice';
import { RequestTypePerPath } from '@Features/router/ess/enum/RequestsRoutePaths.enum';
import { Notification } from '@Types/api/notifications';

import { Drawer } from 'antd';
import { MButton } from '@Components/Base';
import { ViewResolver } from '@Components/ViewResolver';
import { MobileDrawer } from '@Components/index';
import { useAppSelector } from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import NotificationCard from './NotificationCard/NotificationCard';

import styles from './notifications-drawer.module.css';

interface NotificationsDrawerProps {
  isOpen: boolean;
  onCancel: () => void;
  onSearchTask: (recordId: string | number, approveFormPath: string) => void;
  data: Notification[];
}

const headerTitle = (onMarkAllAsRead: () => void): React.ReactNode => {
  return (
    <div className={styles.headerTitle}>
      <div className={styles.headerTitleText}>Notifications</div>
      <MButton
        className={styles.headerTitleButton}
        type="text"
        onClick={onMarkAllAsRead}
      >
        Mark all as read
      </MButton>
    </div>
  );
};

const NotificationsDrawer: React.FC<NotificationsDrawerProps> = ({
  isOpen = true,
  onCancel,
  onSearchTask,
  data,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(currentUserSelector);

  const handleClickMessage = useCallback(
    (index: number, requestTypeName: string, isCreator: boolean) => {
      if (data) {
        dispatch(readMessage(index));
        if (isCreator) {
          const serviceType = requestTypeName === 'Requisition' ? 'mr' : 'ess';
          const path =
            serviceType === 'ess'
              ? RequestTypePerPath[data[index].data.requestTypeID]
              : 'material-request';

          navigate(`/${serviceType}/manage-requests/${path}`);
        }
      }
      onCancel();
    },
    [data, onCancel]
  );

  const handleMarkAllAsRead = useCallback(() => {
    dispatch(markAllAsRead());
  }, [dispatch]);

  const content = useMemo(() => {
    return data?.map((item, index) => (
      <NotificationCard
        key={`${item.data.requestId}~${item.data.approverEmail}`}
        isRead={item.isRead}
        data={item.data}
        receivedTime={item.receivedTime}
        onSearchTask={onSearchTask}
        oncancel={onCancel}
        currentUser={currentUser}
        onclick={() =>
          handleClickMessage(
            index,
            item.data.requestTypeName,
            currentUser.email !== item.data.approverEmail
          )
        }
      />
    ));
  }, [data, handleClickMessage]);

  return (
    <ViewResolver
      desktop={
        <Drawer
          open={isOpen}
          onClose={onCancel}
          className={styles.drawer}
          title={headerTitle(handleMarkAllAsRead)}
          width={410}
        >
          {content}
        </Drawer>
      }
      mobile={
        <MobileDrawer
          title={headerTitle(handleMarkAllAsRead)}
          onClose={onCancel}
          open={isOpen}
        >
          {content}
        </MobileDrawer>
      }
    />
  );
};

export default NotificationsDrawer;
